import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch
} from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ApiError, ProblemType, type IEtagged } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type { IExportPreset, IExportPresetBody } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import type { IAdminTeamFirm } from '../../api/team-firm-contracts';
import { teamFirmsApi } from '../../api/team-firms-api';
import { AdminPage, Fact, LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { useListReread } from '../../components/list-area';
import { BackToList, searchFrom } from '../../components/participant-link';
import { fieldsByCode } from '../../list-criteria';
import { ALL_SURVEYS, LIST_SURVEYS, SEEDED_MARK, catalogueSections, listSurveyLabel } from '../../lists';
import { TEAM_PATHS } from '../../paths';
import { refusalMessage } from '../../refusal';
import { FieldPicker } from '../lists/field-picker';

/**
 * Setup → Export layouts, one layout (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §6) — what a firm's file looks
 * like: its name, its survey (fixed once made; "Any survey" for none), its team firm, its columns —
 * a field under the heading the firm's file expects, in order — its Excel number format and its
 * gridlines. `firm_exchange.manage`, the reads included (the screen's gate; survey-api reads layouts
 * under `responses.browse`, for the export dialog).
 *
 * **Saved whole, under `If-Match`** — the read's `ETag` quoted back as read; a read without one
 * turns Save off. survey-api judges every part (`ExportPresetRules`): no two headings alike,
 * ignoring case; *Internal ID* the participant id's heading alone; a field of the survey's catalogue
 * the caller can see, or a code a firm's file carries (next year's figure); 1 to 200 columns; a
 * number format of printable characters. **Its 400 lands on the box its path names**
 * (`columns[3].heading` on row 4's heading), the draft kept; a 409 `revision-conflict` keeps it with
 * *Reload*; a 404 says the layout is gone. **A seed is edited, never deleted** — Delete is absent and
 * the reason said; 409 `seeded-preset` if it was offered anyway.
 *
 * **Internal ID is always written first**, listed or not (survey-api's `ListExportColumns`), and the
 * form says so. The columns are picked by label from the survey's catalogue — the cross-survey one
 * for "Any survey" — and a stored field the catalogue does not name is kept as spelled.
 */

/** survey-api's default, `ExportPreset`'s. */
const DEFAULT_NUMBER_FORMAT = '#,##0.0';

/** The number formats offered by name; anything else is *Other…*, typed. */
const NUMBER_FORMATS: readonly { format: string; label: string }[] = [
  { format: '#,##0.0', label: '1,234.5 — one decimal' },
  { format: '#,##0', label: '1,235 — whole numbers' },
  { format: '#,##0.00', label: '1,234.50 — two decimals' },
  { format: '0.0%', label: '12.5% — a percent' }
];
const OTHER = '#other';
const NONE = '#none';

/** A column as the form holds it — `key` a React key for the visit. */
interface IColumnDraft {
  key: number;
  field: string;
  heading: string;
}

export interface ILayoutDraft {
  name: string;
  survey: Nullable<string>;
  firm: Nullable<string>;
  columns: IColumnDraft[];
  numberFormat: string;
  gridlines: boolean;
}

let lastKey = 0;
function columnKey(): number {
  lastKey += 1;
  return lastKey;
}

export function emptyLayout(): ILayoutDraft {
  return {
    name: '',
    survey: 'FA',
    firm: null,
    columns: [{ key: columnKey(), field: '', heading: '' }],
    numberFormat: DEFAULT_NUMBER_FORMAT,
    gridlines: true
  };
}

function draftOf(preset: IExportPreset): ILayoutDraft {
  return {
    name: preset.name,
    survey: preset.survey,
    firm: preset.firm,
    columns: preset.columns.map((column) => ({ key: columnKey(), ...column })),
    numberFormat: preset.numberFormat,
    gridlines: preset.gridlines
  };
}

export function layoutBody(draft: ILayoutDraft): IExportPresetBody {
  return {
    name: draft.name,
    survey: draft.survey,
    firm: draft.firm,
    columns: draft.columns.map(({ field, heading }) => ({ field, heading })),
    numberFormat: draft.numberFormat,
    gridlines: draft.gridlines
  };
}

function sameDraft(a: ILayoutDraft, b: ILayoutDraft): boolean {
  return JSON.stringify(layoutBody(a)) === JSON.stringify(layoutBody(b));
}

/** survey-api's refusals by the box they name. */
export type LayoutProblems = Record<string, string[]>;

/** A layout write's refusal, by status and `type`: `problems` at their boxes, `said` above the
 *  form, `conflict` for *Reload*, `gone` for a layout no longer there. */
export function layoutRefusal(
  error: unknown,
  doing: string
): { problems: LayoutProblems; said: Nullable<string>; conflict?: boolean; gone?: boolean } {
  if (!(error instanceof ApiError) || error.status >= 500) {
    return {
      problems: {},
      said: `The answer did not arrive — read the layout again to see whether ${doing} landed.`,
      conflict: true
    };
  }
  const type = error.problem?.type;
  if (error.status === 409 && type === ProblemType.revisionConflict) {
    return {
      problems: {},
      said: 'This layout was changed by someone else since you opened it. Reload it — your changes here are dropped.',
      conflict: true
    };
  }
  if (error.status === 409 && type === ProblemType.seededPreset) {
    return { problems: {}, said: 'A seeded layout is edited, never deleted.' };
  }
  if (error.status === 404) {
    return { problems: {}, said: 'This layout is no longer there — deleted since you opened it.', gone: true };
  }
  if (error.status === 403) {
    return { problems: {}, said: 'Your Team Portal roles do not include changing export layouts.' };
  }
  if (error.status === 428) {
    return { problems: {}, said: 'This page sent the save without the layout’s revision — reload it, then save again.', conflict: true };
  }
  const problems: LayoutProblems = {};
  for (const problem of error.problem?.problems ?? []) {
    problems[problem.target] = [...(problems[problem.target] ?? []), problem.reason];
  }
  return Object.keys(problems).length > 0
    ? { problems, said: null }
    : { problems, said: refusalMessage(error, `The layout could not be saved.`) };
}

/** The register's firms, read once for the form and the list. */
export function useTeamFirms() {
  return useAdminData((signal) => teamFirmsApi.list(signal), []);
}

/** A firm by its label where the register can be read, else as coded. */
export function firmLabel(firms: Nullable<IAdminTeamFirm[]>, code: string): string {
  return firms?.find((firm) => firm.code === code)?.label ?? code;
}

function Problems({ id, reasons }: { id?: string; reasons: readonly string[] | undefined }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <p id={id} className="text-sm text-destructive">
      {reasons.join('; ')}
    </p>
  );
}

/** The paths the form's boxes say a problem at; a problem at any other is said under the form. */
function placedPaths(columns: number): Set<string> {
  const paths = new Set(['name', 'survey', 'firm', 'columns', 'numberFormat', 'gridlines']);
  for (let index = 0; index < columns; index += 1) {
    for (const at of [`columns[${index}]`, `columns[${index}].field`, `columns[${index}].heading`]) paths.add(at);
  }
  return paths;
}

/**
 * The layout's form — create and edit alike; on edit the survey is a fact. Nothing here judges a
 * value: the draft is sent as held, survey-api's 400 placed by path — and **a problem placed by a
 * column's index does not survive the columns moving** (`onReshape`, on ↑, ↓ and ✕: the index
 * would name another column). A problem no box claims is said under the form, never dropped.
 */
export function LayoutForm({
  draft,
  onDraft,
  onReshape,
  surveyFixed,
  problems,
  firms,
  disabled
}: {
  draft: ILayoutDraft;
  onDraft: (draft: ILayoutDraft) => void;
  /** The columns moved or one went — the problems placed by index are the caller's to clear. */
  onReshape: () => void;
  surveyFixed: boolean;
  problems: LayoutProblems;
  firms: { data: Nullable<IAdminTeamFirm[]>; error: Nullable<string> };
  disabled?: boolean;
}) {
  const ids = { name: React.useId(), survey: React.useId(), firm: React.useId(), format: React.useId() };
  const catalogue = useAdminData(
    async (signal) => ({
      survey: draft.survey,
      read: await listsApi.fields(draft.survey ?? ALL_SURVEYS.code, signal)
    }),
    [draft.survey]
  );
  const fields = catalogue.data?.survey === draft.survey ? catalogue.data.read : null;
  const sections = catalogueSections(fields, undefined, new Set(draft.columns.map((column) => column.field)));
  const byCode = fieldsByCode(fields);
  const placed = placedPaths(draft.columns.length);
  const unplaced = Object.entries(problems).flatMap(([at, reasons]) => (placed.has(at) ? [] : reasons));
  const knownFormat = NUMBER_FORMATS.some((one) => one.format === draft.numberFormat);
  const [other, setOther] = React.useState(!knownFormat);

  const set = (change: Partial<ILayoutDraft>) => onDraft({ ...draft, ...change });
  const setColumn = (index: number, change: Partial<IColumnDraft>) =>
    set({ columns: draft.columns.map((column, at) => (at === index ? { ...column, ...change } : column)) });
  const move = (index: number, by: number) => {
    const next = [...draft.columns];
    const [column] = next.splice(index, 1);
    next.splice(index + by, 0, column);
    set({ columns: next });
    onReshape();
  };

  return (
    <div className="grid gap-4 text-sm">
      <div className="grid gap-1.5">
        <Label htmlFor={`${ids.name}-box`}>Name</Label>
        <Input
          id={`${ids.name}-box`}
          value={draft.name}
          disabled={disabled}
          onChange={(event) => set({ name: event.target.value })}
          aria-invalid={problems.name ? true : undefined}
          aria-describedby={ids.name}
          className="max-w-md"
        />
        <Problems id={ids.name} reasons={problems.name} />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor={`${ids.survey}-box`}>Survey</Label>
        {surveyFixed ? (
          <p id={`${ids.survey}-box`}>
            {draft.survey ? listSurveyLabel(draft.survey) : 'Any survey'}
            <span className="text-muted-foreground"> — fixed once the layout was made</span>
          </p>
        ) : (
          <Select
            value={draft.survey ?? NONE}
            disabled={disabled}
            onValueChange={(survey) => set({ survey: survey === NONE ? null : survey })}
          >
            <SelectTrigger id={`${ids.survey}-box`} className="h-9 w-64" aria-describedby={ids.survey}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIST_SURVEYS.map((survey) => (
                <SelectItem key={survey.code} value={survey.code}>
                  {survey.label}
                </SelectItem>
              ))}
              <SelectItem value={NONE}>Any survey</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Problems id={ids.survey} reasons={problems.survey} />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor={`${ids.firm}-box`}>Firm</Label>
        <Select
          value={draft.firm ?? NONE}
          disabled={disabled}
          onValueChange={(firm) => set({ firm: firm === NONE ? null : firm })}
        >
          <SelectTrigger id={`${ids.firm}-box`} className="h-9 w-80" aria-describedby={ids.firm}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>No firm — a named layout</SelectItem>
            {draft.firm !== null && !(firms.data ?? []).some((firm) => firm.code === draft.firm) && (
              <SelectItem value={draft.firm}>{draft.firm}</SelectItem>
            )}
            {(firms.data ?? []).map((firm) => (
              <SelectItem key={firm.code} value={firm.code}>
                {firm.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {firms.error && (
          <p className="text-sm text-muted-foreground">
            The team firm register could not be read: {firms.error}
          </p>
        )}
        <Problems id={ids.firm} reasons={problems.firm} />
      </div>

      <fieldset className="grid gap-2">
        <legend className="mb-1 font-medium">Columns</legend>
        <p className="text-muted-foreground">
          Internal ID is always written first, whether or not it is listed; its heading is{' '}
          <em>Internal ID</em> unless a column names it otherwise.
        </p>
        {catalogue.error && (
          <p className="text-muted-foreground">The survey’s fields could not be read: {catalogue.error}</p>
        )}
        {fields === null && !catalogue.error && draft.survey !== null && catalogue.data && (
          <p className="text-muted-foreground">This survey is not on this deployment.</p>
        )}
        <ol className="grid gap-2">
          {draft.columns.map((column, index) => {
            const at = `columns[${index}]`;
            const fieldProblems = problems[`${at}.field`];
            const headingProblems = problems[`${at}.heading`];
            return (
              <li key={column.key} data-layout-column={index} className="grid gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-6 text-right tabular-nums text-muted-foreground">{index + 1}</span>
                  <FieldPicker
                    label={`Column ${index + 1} — Field`}
                    sections={sections}
                    value={column.field}
                    disabled={disabled}
                    invalid={fieldProblems !== undefined}
                    onChoose={(chosen) => {
                      const label = byCode.get(chosen)?.label;
                      setColumn(index, {
                        field: chosen,
                        heading: column.heading === '' && label ? label : column.heading
                      });
                    }}
                    className="w-72"
                  />
                  <Input
                    aria-label={`Column ${index + 1} — Heading`}
                    value={column.heading}
                    disabled={disabled}
                    onChange={(event) => setColumn(index, { heading: event.target.value })}
                    aria-invalid={headingProblems ? true : undefined}
                    className="h-8 w-64"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Move column ${index + 1} up`}
                    disabled={disabled || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ArrowUp className="size-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Move column ${index + 1} down`}
                    disabled={disabled || index === draft.columns.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ArrowDown className="size-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove column ${index + 1}`}
                    disabled={disabled}
                    onClick={() => {
                      set({ columns: draft.columns.filter((_, other) => other !== index) });
                      onReshape();
                    }}
                  >
                    <X className="size-4" aria-hidden />
                  </Button>
                </div>
                <Problems
                  reasons={[
                    ...(fieldProblems ?? []).map((reason) => `The field ${reason}`),
                    ...(headingProblems ?? []).map((reason) => `The heading ${reason}`),
                    ...(problems[at] ?? [])
                  ]}
                />
              </li>
            );
          })}
        </ol>
        <Button
          type="button"
          variant="outline"
          className="h-8 w-fit gap-1.5 px-2.5 font-normal"
          disabled={disabled}
          onClick={() => set({ columns: [...draft.columns, { key: columnKey(), field: '', heading: '' }] })}
        >
          <Plus className="size-4" aria-hidden />
          Add a column
        </Button>
        <Problems reasons={problems.columns} />
      </fieldset>

      <div className="grid gap-1.5">
        <Label htmlFor={`${ids.format}-box`}>Number format</Label>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={other || !knownFormat ? OTHER : draft.numberFormat}
            disabled={disabled}
            onValueChange={(format) => {
              if (format === OTHER) {
                setOther(true);
              } else {
                setOther(false);
                set({ numberFormat: format });
              }
            }}
          >
            <SelectTrigger id={`${ids.format}-box`} className="h-9 w-64" aria-describedby={ids.format}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NUMBER_FORMATS.map((one) => (
                <SelectItem key={one.format} value={one.format}>
                  {one.label}
                </SelectItem>
              ))}
              <SelectItem value={OTHER}>Other…</SelectItem>
            </SelectContent>
          </Select>
          {(other || !knownFormat) && (
            <Input
              aria-label="Number format — typed"
              value={draft.numberFormat}
              disabled={disabled}
              onChange={(event) => set({ numberFormat: event.target.value })}
              className="h-9 w-48 font-mono"
            />
          )}
        </div>
        <Problems id={ids.format} reasons={problems.numberFormat} />
      </div>

      <label className="flex items-center gap-2">
        <Switch
          checked={draft.gridlines}
          disabled={disabled}
          onCheckedChange={(checked) => set({ gridlines: checked })}
        />
        Gridlines — thin cell borders
      </label>
      <Problems reasons={problems.gridlines} />
      <Problems reasons={unplaced} />
    </div>
  );
}

/** One layout beside the list, remounted per id — a draft never follows the press to another. */
export function AdminExportLayoutPage() {
  const { presetId = '' } = useParams();
  return <LayoutPane key={presetId} presetId={presetId} />;
}

function LayoutPane({ presetId }: { presetId: string }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const rereadList = useListReread();
  const firms = useTeamFirms();
  const { data, error, reload, adopt } = useAdminData<{ found: Nullable<IEtagged<IExportPreset>> }>(
    async (signal) => {
      try {
        return { found: await listsApi.preset(presetId, signal) };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read this layout'));
      }
    },
    [presetId]
  );
  const preset = data?.found?.body ?? null;

  return (
    <AdminPage title={preset?.name ?? 'Export layout'} crumb="Export layouts">
      <BackToList list={TEAM_PATHS.exportLayouts} search={searchFrom(state)} all="All layouts" />
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh — showing the layout as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null ? (
        error ? null : <TableSkeleton rows={4} />
      ) : data.found && preset ? (
        <>
          <LayoutFacts preset={preset} firms={firms.data} />
          <LayoutEditCard
            found={data.found}
            firms={firms}
            onSaved={(saved) => {
              adopt({ found: saved });
              rereadList();
            }}
            onReread={async () => {
              rereadList();
              return reload();
            }}
          />
          <LayoutDeleteCard
            found={data.found}
            onDeleted={() => {
              rereadList();
              navigate(TEAM_PATHS.exportLayouts, { state: { deletedLayout: preset.name } });
            }}
            onReread={() => {
              rereadList();
              void reload();
            }}
          />
        </>
      ) : (
        <Alert>
          <AlertTitle>No such layout</AlertTitle>
          <AlertDescription>
            There is no export layout by this id — it may have been deleted.
          </AlertDescription>
        </Alert>
      )}
    </AdminPage>
  );
}

function LayoutFacts({ preset, firms }: { preset: IExportPreset; firms: Nullable<IAdminTeamFirm[]> }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Layout</CardTitle>
        <CardDescription>
          {preset.seed !== null
            ? `${SEEDED_MARK} — planted by the system; edited here and never deleted.`
            : 'Made on this screen.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
        <Fact label="Survey" value={preset.survey ? listSurveyLabel(preset.survey) : 'Any survey'} />
        <Fact label="Firm" value={preset.firm ? firmLabel(firms, preset.firm) : 'None — a named layout'} />
        <Fact label="Columns" value={preset.columns.length} />
        <Fact
          label="Made"
          value={`${formatDateTime(preset.createdAt)}${preset.createdBy ? ` by ${preset.createdBy.email}` : ''}`}
        />
        <Fact label="Updated" value={formatDateTime(preset.updatedAt)} />
      </CardContent>
    </Card>
  );
}

/**
 * The layout's form, Save and Cancel — `draft` null while nothing changed, so a re-read moves the
 * form unless somebody is editing it; a save goes under the revision the draft started from.
 * **A save's answer is adopted** — the layout and its `ETag` (`onSaved`), the refresh schedule's
 * precedent — so the next save quotes the revision it made, and nothing re-reads it.
 */
function LayoutEditCard({
  found,
  firms,
  onSaved,
  onReread
}: {
  found: IEtagged<IExportPreset>;
  firms: { data: Nullable<IAdminTeamFirm[]>; error: Nullable<string> };
  onSaved: (saved: IEtagged<IExportPreset>) => void;
  onReread: () => Promise<boolean>;
}) {
  const stored = React.useMemo(() => draftOf(found.body), [found.body]);
  const [draft, setDraft] = React.useState<Nullable<ILayoutDraft>>(null);
  const [draftEtag, setDraftEtag] = React.useState<Nullable<string>>(null);
  const [problems, setProblems] = React.useState<LayoutProblems>({});
  const [said, setSaid] = React.useState<Nullable<{ text: string; alert: boolean }>>(null);
  const [conflict, setConflict] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const shown = draft ?? stored;
  const changed = draft !== null && !sameDraft(draft, stored);
  const revision = draftEtag ?? found.etag;

  const edit = (next: ILayoutDraft) => {
    setSaid(null);
    if (draft === null) setDraftEtag(found.etag);
    setDraft(next);
  };
  const discard = () => {
    setDraft(null);
    setDraftEtag(null);
    setProblems({});
    setConflict(false);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!changed || revision === null) return;
    setSaving(true);
    setProblems({});
    setSaid(null);
    try {
      const saved = await listsApi.savePreset(found.body.id, revision, layoutBody(shown));
      onSaved(saved);
      discard();
      setSaid({ text: 'Saved.', alert: false });
    } catch (saveError) {
      const refusal = layoutRefusal(saveError, 'the save');
      setProblems(refusal.problems);
      setConflict(refusal.conflict === true);
      if (refusal.said) setSaid({ text: refusal.said, alert: true });
      if (refusal.gone) void onReread();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-layout-edit>
      <CardHeader>
        <CardTitle>Columns and format</CardTitle>
        <CardDescription>Saved whole — every export and import with this layout uses it at once.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={(event) => void save(event)} noValidate>
          <LayoutForm
            draft={shown}
            onDraft={edit}
            onReshape={() => setProblems({})}
            surveyFixed
            problems={problems}
            firms={firms}
            disabled={saving}
          />
          {found.etag === null && (
            <p className="text-sm text-muted-foreground">
              This layout came without its revision, so a change cannot be sent
              safely. Read the page again.
            </p>
          )}
          {said && (
            <p role={said.alert ? 'alert' : 'status'} className={said.alert ? 'text-sm text-destructive' : 'text-sm'}>
              {said.text}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={!changed || saving || revision === null}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="outline" disabled={draft === null || saving} onClick={discard}>
              Cancel
            </Button>
            {conflict && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  discard();
                  setSaid(null);
                  void onReread();
                }}
              >
                Reload
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** Delete — a confirmed press under `If-Match`; absent on a seed, the reason said in its place. */
function LayoutDeleteCard({
  found,
  onDeleted,
  onReread
}: {
  found: IEtagged<IExportPreset>;
  onDeleted: () => void;
  onReread: () => void;
}) {
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [deleting, setDeleting] = React.useState(false);
  const preset = found.body;

  const remove = async () => {
    if (found.etag === null) return;
    setDeleting(true);
    setSaid(null);
    try {
      await listsApi.deletePreset(preset.id, found.etag);
      onDeleted();
    } catch (deleteError) {
      const refusal = layoutRefusal(deleteError, 'the delete');
      setSaid(refusal.said ?? refusalMessage(deleteError, 'The layout was not deleted.'));
      if (refusal.conflict || refusal.gone) onReread();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card data-layout-delete>
      <CardHeader>
        <CardTitle>Delete</CardTitle>
        <CardDescription>
          {preset.seed !== null
            ? 'A seeded layout is edited, never deleted — the next catalogue rebuild would plant it again.'
            : 'Exports and imports no longer offer it once it is gone; nothing undoes a delete.'}
        </CardDescription>
      </CardHeader>
      {preset.seed === null && (
        <CardContent className="grid gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="outline" className="w-fit" disabled={deleting || found.etag === null}>
                {deleting ? 'Deleting…' : 'Delete the layout'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete the layout {preset.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Exports and imports no longer offer it. Nothing undoes a delete.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => void remove()}>Delete the layout</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {said && (
            <p role="alert" className="text-sm text-destructive">
              {said}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
